import { defineNuxtPlugin } from "#app";
import { isChunkLoadError } from "~/utils/buildVersion";

/**
 * Keeps a long-lived tab from running past the deploy it was loaded with.
 *
 * Two halves, because neither is enough on its own:
 *
 * - polling `/build-info.json` catches the deploy early, while the tab is still
 *   working, so the user is asked to reload at a moment of their choosing —
 *   which matters here, where a reload can land in the middle of a live
 *   notarization;
 * - the error listeners catch the deploy that lands between two polls, when the
 *   first failed import has already broken part of the screen.
 *
 * Both ends in the same place: the banner. Nothing here reloads the page.
 *
 * @see composables/useAppUpdate for the state.
 */

/** Long enough to be invisible, short enough that a stale tab is minutes old. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/** Focus and visibility fire in bursts; one network round trip is plenty. */
const MIN_CHECK_INTERVAL_MS = 60 * 1000;

/** Where the hashed chunks live, for resource errors that carry no message. */
const BUILD_ASSETS_PATH = "/_nuxt/";

export default defineNuxtPlugin((nuxtApp) => {
  // Everything below reaches for `window` and `document` at setup. The
  // `.client` suffix is what keeps this file out of the server bundle; this
  // guard is what survives a rename, and it costs nothing — the bundler folds
  // it away. Dev is excluded for its own reason: there is no build-info.json,
  // and HMR produces the very import errors this watches for, so reloading the
  // page under the developer would be its own bug.
  if (!import.meta.client || import.meta.dev) return;

  const {
    checkForUpdate,
    lastCheckedAt,
    markChunkFailed,
    runningBuildId,
    updateAvailable,
  } = useAppUpdate();

  // Without an id there is nothing to compare against — a build that never ran
  // the deploy hook. The error listeners below still apply.
  const canPoll = runningBuildId.length > 0;

  const checkIfDue = () => {
    if (updateAvailable.value) return;
    if (Date.now() - lastCheckedAt.value < MIN_CHECK_INTERVAL_MS) return;

    void checkForUpdate();
  };

  /**
   * Raises the banner and says why in the console. Deliberately the whole of
   * the response: the page is never reloaded out from under whoever is using
   * it. @see composables/useAppUpdate
   */
  const reportBrokenBuild = (detail: unknown) => {
    console.warn("Chunk load failed; the tab is on an old build", detail);
    markChunkFailed();
  };

  /**
   * A `<script>` or `<link>` that 404s reports through window `error` with no
   * message at all — only the element. If it points at the build assets, the
   * deploy moved out from under us.
   */
  const handleResourceError = (event: ErrorEvent) => {
    const error = event.error ?? event.message;
    if (isChunkLoadError(error)) {
      reportBrokenBuild(error);
      return;
    }

    const target = event.target as HTMLScriptElement & HTMLLinkElement;
    const source = target?.src || target?.href || "";
    if (source.includes(BUILD_ASSETS_PATH)) {
      reportBrokenBuild(`Failed to load build asset: ${source}`);
    }
  };

  // Nuxt emits this for exactly this failure, so it needs no filtering of its
  // own. `emitRouteChunkError: "manual"` in nuxt.config is what keeps Nuxt's
  // own reload-on-navigation from firing behind our backs.
  nuxtApp.hook("app:chunkError", ({ error }) => reportBrokenBuild(error));

  // Dynamic imports outside routing — lazy components, the viewer, modals.
  window.addEventListener("unhandledrejection", (event) => {
    if (isChunkLoadError(event.reason)) reportBrokenBuild(event.reason);
  });

  // Capture phase: resource errors do not bubble.
  window.addEventListener("error", handleResourceError, true);

  if (!canPoll) return;

  window.setInterval(checkIfDue, POLL_INTERVAL_MS);

  // The common shape is a tab left open overnight: the interval fired while it
  // was hidden, but this is the moment the user comes back to it.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") checkIfDue();
  });

  // Restored from the back/forward cache, where timers were frozen.
  window.addEventListener("pageshow", (event) => {
    if ((event as PageTransitionEvent).persisted) checkIfDue();
  });
});
